// Minimal typings and loader for MONEI Components (monei.js v3).
// MONEI requires loading the script from js.monei.com rather than bundling it.
// https://docs.monei.com/monei-js/reference/

export type MoneiAddress = {
  country?: string
  city?: string
  line1?: string
  line2?: string
  zip?: string
  state?: string
}

export type MoneiDetails = {
  name?: string
  email?: string
  phone?: string
  company?: string
  address?: MoneiAddress
}

export type MoneiShippingOption = {
  id: string
  label: string
  description?: string
  amount: number
  selected?: boolean
}

export type MoneiSubmitResult = {
  token?: string
  error?: string
  paymentMethod?: string
  finalAmount?: number
  billingDetails?: MoneiDetails
  shippingDetails?: MoneiDetails
  shippingOption?: MoneiShippingOption
}

export type MoneiPaymentRequestOptions = {
  accountId: string
  sessionId: string
  amount: number
  currency: string
  language?: string
  requestShipping?: boolean
  requestBilling?: boolean
  shippingOptions?: MoneiShippingOption[]
  onShippingAddressChange?: (
    address: MoneiAddress
  ) => Promise<{ shippingOptions?: MoneiShippingOption[]; amount?: number }>
  onShippingOptionChange?: (
    option: MoneiShippingOption
  ) => Promise<{ amount?: number }>
  onSubmit: (result: MoneiSubmitResult) => void
  onBeforeOpen?: () => boolean
  onLoad?: (isSupported: boolean) => void
  onError?: (error: Error) => void
  style?: {
    height?: number | string
    color?: "default" | "black" | "white"
    type?: "buy" | "plain" | "donate"
    borderRadius?: number | string
  }
}

export type MoneiComponent = {
  render: (container: string | HTMLElement) => void
  updateProps: (props: Partial<MoneiPaymentRequestOptions>) => void
  destroy: () => void
}

export type MoneiJs = {
  PaymentRequest: (options: MoneiPaymentRequestOptions) => MoneiComponent
}

declare global {
  interface Window {
    monei?: MoneiJs
  }
}

const MONEI_JS_URL = "https://js.monei.com/v3/monei.js"

let loading: Promise<MoneiJs> | null = null

export function loadMoneiJs(): Promise<MoneiJs> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("monei.js can only load in the browser"))
  }
  if (window.monei) {
    return Promise.resolve(window.monei)
  }
  loading ??= new Promise<MoneiJs>((resolve, reject) => {
    const script = document.createElement("script")
    script.src = MONEI_JS_URL
    script.async = true
    script.onload = () =>
      window.monei ? resolve(window.monei) : reject(new Error("monei.js not available"))
    script.onerror = () => {
      loading = null
      script.remove()
      reject(new Error("Could not load monei.js"))
    }
    document.head.appendChild(script)
  })
  return loading
}

/** Unique id shared by the wallet component and the server-side payment. */
export function newMoneiSessionId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`
}
